import { toast } from 'sonner';
import { api, devToken } from './api';
import { db } from './db';
import { commit } from './store';
import { stripCode } from './architect';
import { extractTitle } from './analyze';
import { makePoster } from './poster';
import { reportKeyStatus, reportJobError } from './keyStatus';

const ID_KEY = 'titan_active_generation_job_id';
const META_KEY = 'titan_active_generation_job';
const ACTIVE = ['queued', 'processing'];
const TERMINAL = ['completed', 'failed', 'cancelled', 'timed_out'];
const chan = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('titan_job_sync') : null;

let state = { job: null, meta: null, failed: null };
let timer = null;
let lastRemote = 0;
const subs = new Set();
const handled = new Set();

const readMeta = () => { try { return JSON.parse(localStorage.getItem(META_KEY)); } catch { return null; } };
const emit = () => subs.forEach((f) => f(state));
const set = (patch) => { state = { ...state, ...patch }; emit(); };
const clearActive = () => { localStorage.removeItem(ID_KEY); localStorage.removeItem(META_KEY); };

export const jobs = {
  get: () => state,
  subscribe(fn) { subs.add(fn); fn(state); return () => subs.delete(fn); },

  async start(body, meta) {
    if (state.job && ACTIVE.includes(state.job.status)) throw new Error('Un altro gioco è già in generazione: attendi o annullalo');
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
    const { api_key: _k, ...safe } = body; // the key is never persisted on the device for retries
    const r = await api('/jobs/create', { method: 'POST', body, auth: true, timeout: 20000 });
    const m = { ...meta, id: r.jobId, request: { ...safe, history: undefined, code: undefined } };
    localStorage.setItem(ID_KEY, r.jobId);
    localStorage.setItem(META_KEY, JSON.stringify(m));
    set({ job: { id: r.jobId, status: 'queued', progress_percent: 0, current_phase: 'In coda…' }, meta: m, failed: null });
    chan?.postMessage({ type: 'job', job: state.job, meta: m });
    schedule(300);
    return r.jobId;
  },

  async cancel() {
    const id = state.job?.id;
    if (!id) return;
    stop();
    try { const j = await api(`/jobs/${id}/cancel`, { method: 'POST', auth: true }); await onUpdate(j, true); }
    catch { await onUpdate({ ...state.job, status: 'cancelled' }, true); }
  },

  dismissFailure() { set({ failed: null }); },

  async resume() {
    const id = localStorage.getItem(ID_KEY);
    if (!id || !devToken.get() || state.job?.id === id) return;
    set({ job: { id, status: 'queued', progress_percent: 0, current_phase: 'Riconnessione…' }, meta: readMeta() });
    await poll();
  },
};

async function checkKey(j) {
  if (j.ai_mode !== 'byok') return;
  reportJobError(j.provider, j.error_code);
  for (const wait of [3000, 6000]) {
    await new Promise((r) => setTimeout(r, wait));
    const f = await api(`/jobs/${j.id}`, { auth: true }).catch(() => null);
    if (f?.key_status) { reportKeyStatus(f.key_status, { silent: !f.key_status.low?.length && !f.key_status.exhausted }); return; }
  }
}

function stop() { clearTimeout(timer); timer = null; }
function schedule(ms = 2500 + (Math.random() * 600 - 300)) { stop(); timer = setTimeout(poll, ms); }

async function poll() {
  const id = state.job?.id;
  if (!id) return;
  if (Date.now() - lastRemote < 2200) return schedule(); // another tab just polled: reuse its broadcast
  try {
    const j = await api(`/jobs/${id}`, { auth: true, timeout: 15000 });
    await onUpdate(j, true);
  } catch (e) {
    if (e.status === 404) { clearActive(); set({ job: null, meta: null }); return; }
    if (e.status === 401) { stop(); return; }
  }
  if (state.job && ACTIVE.includes(state.job.status)) schedule();
}

async function onUpdate(j, local) {
  const meta = state.meta || readMeta();
  set({ job: j, meta });
  if (local) chan?.postMessage({ type: 'job', job: j, meta });
  if (!TERMINAL.includes(j.status) || handled.has(j.id)) return;
  handled.add(j.id);
  if (local) checkKey(j);
  stop();
  clearActive();
  if (j.status === 'completed') {
    const { apply } = await api(`/jobs/${j.id}/ack`, { method: 'POST', auth: true }).catch(() => ({ apply: false }));
    if (apply) { await applyResult(j, meta); chan?.postMessage({ type: 'applied', gameId: meta?.gameId, jobId: j.id }); }
    finishUi(j, meta, apply);
    set({ job: null, meta: null });
  } else {
    set({ job: null, meta: null, failed: j.status === 'cancelled' ? null : { job: j, meta } });
    if (j.status !== 'cancelled') toast.error(j.error_message || 'Generazione non riuscita', { id: `job-${j.id}` });
    else toast('Generazione annullata', { id: `job-${j.id}` });
  }
}

async function applyResult(j, meta) {
  const g = meta?.gameId && await db.getGame(meta.gameId);
  if (!g) return;
  const text = j.result_text || '';
  const code = j.result_code;
  const base = code ? commit(g, code, meta.label || 'Architect', meta.userText) : g;
  const next = { ...base, title: code && g.blank ? extractTitle(code, g.title) : g.title, blank: code ? false : g.blank, updatedAt: Date.now(),
    chat: [...g.chat, { role: 'assistant', content: stripCode(text), ts: Date.now(), versionId: code ? base.headId : null, ...(meta.mode === 'plan' && !code ? { plan: true } : {}) }] };
  await db.putGame(next);
  if (code && g.blank) { try { await db.putGame({ ...next, cover: await makePoster({ title: next.title, genre: next.genre }) }); } catch { /* cover is optional */ } }
  window.dispatchEvent(new CustomEvent('titan:job-applied', { detail: { gameId: meta.gameId, code: !!code } }));
}

function finishUi(j, meta, applied) {
  if (j.result_code) toast.success('Nuovo gioco generato con successo!', { id: `job-${j.id}`, description: meta?.label });
  else toast.success("Risposta dell'Architetto pronta", { id: `job-${j.id}` });
  if (!applied) return;
  chime();
  if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try { const n = new Notification('Titan Game Studio', { body: '🎮 Il tuo gioco è pronto per essere testato!', icon: '/favicon.png', tag: 'job-completed' }); n.onclick = () => { window.focus(); n.close(); }; } catch { /* unsupported */ }
  }
}

function chime() {
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    const a = new C(); const t = a.currentTime;
    [[880, 0], [1318, 0.12]].forEach(([f, d]) => {
      const o = a.createOscillator(); const g = a.createGain();
      o.type = 'sine'; o.frequency.value = f; g.gain.setValueAtTime(0.08, t + d); g.gain.exponentialRampToValueAtTime(0.0005, t + d + 0.35);
      o.connect(g); g.connect(a.destination); o.start(t + d); o.stop(t + d + 0.4);
    });
    setTimeout(() => a.close(), 900);
  } catch { /* audio blocked */ }
}

chan?.addEventListener('message', (e) => {
  const m = e.data || {};
  if (m.type === 'job' && m.job) {
    lastRemote = Date.now();
    if (!state.job || state.job.id === m.job.id || ACTIVE.includes(m.job.status)) {
      if (m.meta) state.meta = m.meta;
      onUpdate(m.job, false);
      if (ACTIVE.includes(m.job.status) && !timer) schedule();
    }
  } else if (m.type === 'applied') {
    window.dispatchEvent(new CustomEvent('titan:job-applied', { detail: { gameId: m.gameId } }));
  }
});
window.addEventListener('storage', (e) => { if (e.key === ID_KEY && e.newValue && !state.job) jobs.resume(); });
document.addEventListener('visibilitychange', () => { if (!document.hidden && state.job) schedule(200); });
